import React from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import GetAppIcon from "@material-ui/icons/GetApp";
import ReceiptIcon from "@material-ui/icons/Receipt";
import { useTranslation } from "react-i18next";

import {
  formatCents,
  formatInvoicePeriod,
  localeFromLanguage,
} from "../../../utils/commissionInvoiceFormat";

const useStyles = makeStyles((theme) => ({
  card: {
    backgroundColor: "white",
    borderRadius: "24px",
    padding: "32px 40px",
    boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.05)",
    border: "1px solid #E0E0E0",
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down("xs")]: {
      padding: "24px 16px",
    },
  },
  header: {
    display: "flex",
    alignItems: "center",
    marginBottom: 24,
  },
  tableContainer: {
    // Le tableau ne doit jamais élargir la page : il scrolle dans son cadre.
    overflowX: "auto",
    marginTop: theme.spacing(2),
  },
  headerCell: {
    fontWeight: 600,
    color: "#697386",
    fontSize: "0.8rem",
    whiteSpace: "nowrap",
  },
  cell: {
    fontSize: "0.875rem",
    color: "#1a1f36",
    whiteSpace: "nowrap",
  },
  emptyState: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(4),
    color: "#697386",
    fontSize: "0.9rem",
  },
  downloadButton: {
    textTransform: "none",
    borderRadius: "50px",
    backgroundColor: "#3F51B5",
    color: "white",
    fontSize: "0.8rem",
    padding: "4px 16px",
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#32408F",
    },
  },
  sentChip: {
    display: "inline-block",
    padding: "2px 10px",
    borderRadius: "50px",
    fontSize: "0.75rem",
    fontWeight: 600,
  },
  sent: {
    backgroundColor: "#DCFCE7",
    color: "#166534",
  },
  pending: {
    backgroundColor: "#F3F4F6",
    color: "#697386",
  },
}));

const CommissionInvoicesCard = ({
  invoices,
  isLoading,
  downloadingInvoiceId,
  onDownload,
}) => {
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  const locale = localeFromLanguage(i18n.language);

  return (
    <Box className={classes.card}>
      <Box className={classes.header}>
        <ReceiptIcon
          style={{ fontSize: 28, color: "#3F51B5", marginRight: 12 }}
        />
        <Typography variant="h6" style={{ fontWeight: 600 }}>
          {t("commission_invoices.title")}
        </Typography>
      </Box>
      <Divider style={{ borderColor: "#E0E0E0" }} />

      {isLoading ? (
        <Box className={classes.emptyState}>
          <CircularProgress size={28} />
        </Box>
      ) : !invoices || invoices.length === 0 ? (
        <Typography className={classes.emptyState}>
          {t("commission_invoices.empty")}
        </Typography>
      ) : (
        <TableContainer className={classes.tableContainer}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell className={classes.headerCell}>
                  {t("commission_invoices.number")}
                </TableCell>
                <TableCell className={classes.headerCell}>
                  {t("commission_invoices.period")}
                </TableCell>
                <TableCell className={classes.headerCell} align="right">
                  {t("commission_invoices.total_ht")}
                </TableCell>
                <TableCell className={classes.headerCell} align="right">
                  {t("commission_invoices.vat")}
                </TableCell>
                <TableCell className={classes.headerCell} align="right">
                  {t("commission_invoices.total_ttc")}
                </TableCell>
                <TableCell className={classes.headerCell}>
                  {t("commission_invoices.status")}
                </TableCell>
                <TableCell className={classes.headerCell} align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {invoices.map((invoice) => (
                <TableRow key={invoice.id}>
                  <TableCell className={classes.cell}>
                    {invoice.invoiceNumber}
                  </TableCell>
                  <TableCell className={classes.cell}>
                    {formatInvoicePeriod(
                      invoice.periodMonth,
                      invoice.periodYear,
                      locale
                    )}
                  </TableCell>
                  <TableCell className={classes.cell} align="right">
                    {formatCents(invoice.totalHT, locale, invoice.currency)}
                  </TableCell>
                  <TableCell className={classes.cell} align="right">
                    {formatCents(invoice.totalVat, locale, invoice.currency)}
                    {invoice.vatRate !== null && invoice.vatRate !== undefined
                      ? ` (${invoice.vatRate}%)`
                      : ""}
                  </TableCell>
                  <TableCell className={classes.cell} align="right">
                    {formatCents(invoice.totalTTC, locale, invoice.currency)}
                  </TableCell>
                  <TableCell className={classes.cell}>
                    <span
                      className={`${classes.sentChip} ${
                        invoice.sentAt ? classes.sent : classes.pending
                      }`}
                    >
                      {invoice.sentAt
                        ? t("commission_invoices.sent_on", {
                            date: new Date(invoice.sentAt).toLocaleDateString(
                              locale
                            ),
                          })
                        : t("commission_invoices.not_sent")}
                    </span>
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      type="button"
                      variant="contained"
                      size="small"
                      className={classes.downloadButton}
                      onClick={() => onDownload(invoice.id)}
                      disabled={downloadingInvoiceId === invoice.id}
                      startIcon={
                        downloadingInvoiceId === invoice.id ? null : (
                          <GetAppIcon style={{ fontSize: 18 }} />
                        )
                      }
                    >
                      {downloadingInvoiceId === invoice.id ? (
                        <CircularProgress size={16} style={{ color: "white" }} />
                      ) : (
                        t("commission_invoices.download")
                      )}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default CommissionInvoicesCard;
